/**
 * DID Service - Handles all DID number-related API operations
 * Following the same pattern as sipTrunkService with modern API client
 */

import { apiClient, ApiClientError } from './apiClient';
import type { SortItem, PaginatedResults, ApiResponse } from '@/types/common';

// DID Number Types
export interface DidNumber {
  id: number;
  number: string;
  description: string;
  custMaxChannels: number;
  isSmsEnabled: boolean;
  monthlyRate: number;
  nextBillingDate: string;
  dateAdded: string;
  activationFee: number;
  commitmentTimeTillCustomer: string | null;
  ratePerMin: number;
  countryCode: string;
  isQuarantined?: boolean;
  hasCustomer?: boolean;
}

export interface DidNumberFilters {
  search?: string;
  smsEnabled?: {
    value: string | null; // 'true' for enabled, 'false' for disabled
  };
  channelRange?: {
    value: [number | null, number | null] | null;
  };
  dateRange?: {
    value: [Date | string | null, Date | string | null] | null;
  };
  page?: number;
  pageSize?: number;
}

export interface DidNumberCreateData {
  number: string;
  description?: string;
  custMaxChannels?: number;
  isSmsEnabled?: boolean;
  monthlyRate?: number;
  activationFee?: number;
  ratePerMin?: number;
}

export interface DidNumberUpdateData {
  description?: string;
  custMaxChannels?: number;
  isSmsEnabled?: boolean;
  monthlyRate?: number;
  activationFee?: number;
  ratePerMin?: number;
}

export interface DidNumberResponse {
  success: boolean;
  data: DidNumber[];
  meta?: {
    pagination?: {
      page: number;
      per_page: number;
      total: number;
      total_pages: number;
      has_previous: boolean;
      has_next: boolean;
    };
  };
  message?: string;
}

export interface DidNumberInitData {
  number: string;
}

// Custom error class for DID API
export class DidApiError extends Error {
  constructor(
    message: string,
    public code?: string,
    public details?: any
  ) {
    super(message);
    this.name = 'DidApiError';
  }
}

class DidService {
  private readonly baseEndpoint = 'numbers';

  /**
   * Fetch DID numbers with optional filters
   */
  async getDidNumbers(filters?: DidNumberFilters): Promise<DidNumberResponse> {
    try {
      const params = this.buildQueryParams(filters);
      
      const response = await apiClient.getPaginated<DidNumber>(
        `${this.baseEndpoint}/`,
        params
      );

      // Extract pagination from meta
      const pagination = response.meta?.pagination || {
        page: 1,
        per_page: 25,
        total: 0,
        total_pages: 0,
        has_previous: false,
        has_next: false
      };

      const numbers = Array.isArray(response.data) ? response.data : [];

      return {
        success: true,
        data: numbers,
        meta: {
          pagination
        }
      };
    } catch (error) {
      console.error('Error fetching DID numbers:', error);
      
      if (error instanceof ApiClientError) {
        throw new DidApiError(
          error.message,
          error.code,
          error.details
        );
      }
      
      throw new DidApiError(
        'Failed to fetch DID numbers. Please try again.',
        'NETWORK_ERROR',
        error
      );
    }
  }

  /**
   * Export DID numbers to CSV
   */
  async exportDidNumbers(filters?: DidNumberFilters): Promise<void> {
    try {
      const params = this.buildQueryParams(filters);
      // Add export parameter
      params.format = 'csv';
      
      await apiClient.downloadFile(
        `${this.baseEndpoint}/`,
        params,
        `did-numbers-export-${new Date().toISOString().split('T')[0]}.csv`
      );
    } catch (error) {
      console.error('Error exporting DID numbers:', error);
      throw new DidApiError(
        'Failed to export DID numbers. Please try again.',
        'EXPORT_ERROR',
        error
      );
    }
  }

  /**
   * Get DID number details by ID
   */
  async getDidNumberById(id: number): Promise<DidNumber> {
    try {
      const response = await apiClient.getModern<DidNumber>(`${this.baseEndpoint}/${id}/`);
      
      if (apiClient.isSuccessResponse(response)) {
        return response.data;
      }
      
      throw new DidApiError(
        apiClient.getErrorMessage(response),
        apiClient.getErrorCode(response),
        apiClient.getErrorDetails(response)
      );
    } catch (error) {
      console.error('Error fetching DID number details:', error);
      
      if (error instanceof DidApiError) {
        throw error;
      }
      
      if (error instanceof ApiClientError) {
        throw new DidApiError(
          error.message,
          error.code,
          error.details
        );
      }
      
      throw new DidApiError(
        'Failed to fetch DID number details. Please try again.',
        'FETCH_ERROR',
        error
      );
    }
  }

  /**
   * Initialize DID number creation (if needed for validation)
   */
  async initDidNumberCreation(): Promise<DidNumberInitData> {
    try {
      const response = await apiClient.getModern<DidNumberInitData>(`${this.baseEndpoint}/init/`);
      
      if (apiClient.isSuccessResponse(response)) {
        return response.data;
      }
      
      throw new DidApiError(
        apiClient.getErrorMessage(response),
        apiClient.getErrorCode(response),
        apiClient.getErrorDetails(response)
      );
    } catch (error) {
      console.error('Error initializing DID number creation:', error);
      
      if (error instanceof DidApiError) {
        throw error;
      }
      
      if (error instanceof ApiClientError) {
        throw new DidApiError(
          error.message,
          error.code,
          error.details
        );
      }
      
      throw new DidApiError(
        'Failed to initialize DID number creation. Please try again.',
        'INIT_ERROR',
        error
      );
    }
  }

  /**
   * Create a new DID number
   */
  async createDidNumber(data: DidNumberCreateData): Promise<DidNumber> {
    try {
      const response = await apiClient.postModern<DidNumber>(`${this.baseEndpoint}/`, data);
      
      if (apiClient.isSuccessResponse(response)) {
        return response.data;
      }
      
      throw new DidApiError(
        apiClient.getErrorMessage(response),
        apiClient.getErrorCode(response),
        apiClient.getErrorDetails(response)
      );
    } catch (error) {
      console.error('Error creating DID number:', error);
      
      if (error instanceof DidApiError) {
        throw error;
      }
      
      if (error instanceof ApiClientError) {
        throw new DidApiError(
          error.message,
          error.code,
          error.details
        );
      }
      
      throw new DidApiError(
        'Failed to create DID number. Please try again.',
        'CREATE_ERROR',
        error
      );
    }
  }

  /**
   * Update an existing DID number
   */
  async updateDidNumber(id: number, data: DidNumberUpdateData): Promise<DidNumber> {
    try {
      const response = await apiClient.putModern<DidNumber>(`${this.baseEndpoint}/${id}/`, data);
      
      if (apiClient.isSuccessResponse(response)) {
        return response.data;
      }
      
      throw new DidApiError(
        apiClient.getErrorMessage(response),
        apiClient.getErrorCode(response),
        apiClient.getErrorDetails(response)
      );
    } catch (error) {
      console.error('Error updating DID number:', error);
      
      if (error instanceof DidApiError) {
        throw error;
      }
      
      if (error instanceof ApiClientError) {
        throw new DidApiError(
          error.message,
          error.code,
          error.details
        );
      }
      
      throw new DidApiError(
        'Failed to update DID number. Please try again.',
        'UPDATE_ERROR',
        error
      );
    }
  }

  /**
   * Delete a DID number
   */
  async deleteDidNumber(id: number): Promise<void> {
    try {
      await apiClient.deleteModern(`${this.baseEndpoint}/${id}/`);
    } catch (error) {
      console.error('Error deleting DID number:', error);
      
      if (error instanceof DidApiError) {
        throw error;
      }
      
      if (error instanceof ApiClientError) {
        throw new DidApiError(
          error.message,
          error.code,
          error.details
        );
      }
      
      throw new DidApiError(
        'Failed to delete DID number. Please try again.',
        'DELETE_ERROR',
        error
      );
    }
  }

  /**
   * Build query parameters for API requests
   */
  private buildQueryParams(filters?: DidNumberFilters): Record<string, string | number | null> {
    const params: Record<string, string | number | null> = {};

    if (!filters) return params;

    // Search
    if (filters.search?.trim()) {
      params.search = filters.search.trim();
    }

    // SMS enabled filter
    if (filters.smsEnabled?.value !== null && filters.smsEnabled?.value !== undefined) {
      params.is_sms_enabled = filters.smsEnabled.value;
    }

    // Channel range filter
    if (filters.channelRange?.value) {
      const [min, max] = filters.channelRange.value;
      if (min !== null && min !== undefined) {
        params.max_channels_min = min;
      }
      if (max !== null && max !== undefined) {
        params.max_channels_max = max;
      }
    }

    // Date range filter
    if (filters.dateRange?.value) {
      const [startDate, endDate] = filters.dateRange.value;
      if (startDate) {
        params.added_after = this.formatDateForAPI(startDate);
      }
      if (endDate) {
        params.added_before = this.formatDateForAPI(endDate);
      }
    }

    // Pagination
    if (filters.page) {
      params.page = filters.page;
    }
    if (filters.pageSize) {
      params.page_size = filters.pageSize;
    }

    return params;
  }

  /**
   * Format date for API consumption
   */
  private formatDateForAPI(date: Date | string | null): string | null {
    if (!date) return null;
    
    if (typeof date === 'string') {
      // If it's already a string, try to parse it and format it properly
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) {
        return date; // Return as-is if can't parse
      }
      // Format as YYYY-MM-DD for Django DateFilter
      return parsedDate.toISOString().split('T')[0];
    }
    
    if (date instanceof Date) {
      // Format as YYYY-MM-DD for Django DateFilter
      return date.toISOString().split('T')[0];
    }
    
    return null;
  }

  /**
   * Get SMS status display text
   */
  getSmsStatusDisplay(isSmsEnabled: boolean): string {
    return isSmsEnabled ? 'Enabled' : 'Disabled';
  }

  /**
   * Get SMS status color
   */
  getSmsStatusColor(isSmsEnabled: boolean): string {
    return isSmsEnabled ? 'success' : 'error';
  }

  /**
   * Get quarantine status display text
   */
  getQuarantineStatusDisplay(isQuarantined: boolean): string {
    return isQuarantined ? 'Quarantined' : 'Active';
  }

  /**
   * Get quarantine status color
   */
  getQuarantineStatusColor(isQuarantined: boolean): string {
    return isQuarantined ? 'warning' : 'success';
  }
}

// Export singleton instance
export const didService = new DidService();
export default didService; 